import React, { useState } from 'react';
import {
    Box,
    Paper,
    TablePagination,
    TableSortLabel,
    Checkbox,
    Avatar,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    CircularProgress,
    Button
} from '@mui/material';
import {
    RefreshIcon,
    MoreVertIcon,
    ContentCopyIcon,
    OpenInNewIcon,
    EditOutlinedIcon,
    DeleteOutlineIcon,
    DownloadIcon
} from 'icons/materialIcons';
import { Salesforce } from 'utils/icons/integrationsSvgIcons';
import LeadSourceChip from 'components/contacts/contactMain/contactsTable/LeadSourceChip';
import PropTypes from 'prop-types';

const TeamV2LeadsTable = ({
    leads = [],
    onLeadClick,
    isLoading = false,
    onRefresh,
    onSelectLeads,
    selectedLeadIds = []
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selected, setSelected] = useState(selectedLeadIds || []);
    const [orderBy, setOrderBy] = useState('date');
    const [order, setOrder] = useState('desc');
    const [hoveredRow, setHoveredRow] = useState(null);

    // Check if leads is a valid array
    const isValidLeadsArray = Array.isArray(leads) && leads.length > 0;

    // Sync selected state with parent component
    React.useEffect(() => {
        setSelected(selectedLeadIds);
    }, [selectedLeadIds]);

    // Loading state
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2, color: '#666' }}>Loading team leads...</Typography>
            </Box>
        );
    }

    // Empty or invalid state
    if (!isValidLeadsArray) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: 300,
                border: '0.0625rem dashed #e0e0e0',
                borderRadius: '0.75rem',
                backgroundColor: '#fafafa'
            }}>
                <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                    No team leads found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                    Add your first team lead by clicking the 'Add Leads' button above
                </Typography>
                {onRefresh && (
                    <Button
                        startIcon={<RefreshIcon />}
                        variant="outlined"
                        onClick={onRefresh}
                        sx={{
                            mt: 1,
                            borderColor: '#e0e0e0',
                            color: '#666',
                            '&:hover': {
                                borderColor: '#29AEF8',
                                color: '#29AEF8'
                            }
                        }}
                    >
                        Refresh
                    </Button>
                )}
            </Box>
        );
    }

    const getInitials = (name) => {
        if (!name) return '';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase();
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = leads.map(n => n._id);
            setSelected(newSelected);
            onSelectLeads && onSelectLeads(newSelected);
            return;
        }
        setSelected([]);
        onSelectLeads && onSelectLeads([]);
    };

    const handleClick = (event, lead) => {
        if (!lead || !lead._id) {
            console.error("Invalid lead object:", lead);
            return;
        }

        if (event.target.closest('.MuiCheckbox-root') || event.target.closest('[data-copy-button]')) {
            const selectedIndex = selected.indexOf(lead._id);
            let newSelected = [];

            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, lead._id);
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1));
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(
                    selected.slice(0, selectedIndex),
                    selected.slice(selectedIndex + 1),
                );
            }

            setSelected(newSelected);
            onSelectLeads && onSelectLeads(newSelected);
        } else if (!event.target.closest('.MuiIconButton-root')) {
            onLeadClick && onLeadClick(lead);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = () => {
        const isAsc = orderBy === 'date' && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy('date');
    };

    const handleCopyEmail = async (e, email, id) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(email);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    // Sort leads by date
    const sortedLeads = [...leads].sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;

        if (order === 'asc') {
            return new Date(a.createdAt) - new Date(b.createdAt);
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
    });

    const emptyRows = page > 0
        ? Math.max(0, (1 + page) * rowsPerPage - leads.length)
        : 0;

    // Row hover handlers
    const handleRowMouseEnter = (leadId) => {
        setHoveredRow(leadId);
    };

    const handleRowMouseLeave = () => {
        setHoveredRow(null);
    };

    // Format date for display
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'inactive':
                return 'default';
            default:
                return 'default';
        }
    };

    return (
        <Box sx={{ width: '100%', overflow: 'auto' }}>
            {/* Column Headers - Completely transparent background */}
            <Box sx={{
                width: '100%',
                mb: 0.5,
                background: 'transparent',
                backgroundColor: 'transparent'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    minWidth: '62.5rem',
                    paddingLeft: '0.75rem',
                    paddingRight: '0.75rem',
                    background: 'transparent',
                    backgroundColor: 'transparent',
                    height: '4.5rem'
                }}>
                    <Box sx={{
                        flex: '0 0 25rem',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '1rem 0.75rem',
                        background: 'transparent',
                        backgroundColor: 'transparent'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.5rem 1rem 0.5rem 1rem',
                            background: '#ffffff',
                            backgroundColor: '#ffffff',
                            borderRadius: '0.75rem',
                            mr: 2
                        }}>
                            <Checkbox
                                color="primary"
                                indeterminate={selected.length > 0 && selected.length < leads.length}
                                checked={leads.length > 0 && selected.length === leads.length}
                                onChange={handleSelectAllClick}
                                inputProps={{ 'aria-label': 'select all leads' }}
                                sx={{
                                    color: '#d0d0d0',
                                    padding: '0',
                                    marginRight: '0.5rem',
                                    '&.Mui-checked': {
                                        color: '#a0a0a0'
                                    },
                                    '&.MuiCheckbox-indeterminate': {
                                        color: '#a0a0a0'
                                    },
                                    '& .MuiSvgIcon-root': {
                                        borderRadius: '0.5rem',
                                        backgroundColor: '#fff'
                                    }
                                }}
                            />
                            <Typography variant="body2" sx={{
                                fontWeight: 600,
                                color: '#666',
                                userSelect: 'none',
                                whiteSpace: 'nowrap',
                                fontSize: '0.875rem',
                                background: 'transparent',
                                backgroundColor: 'transparent'
                            }}>
                                Select All
                            </Typography>
                        </Box>
                        <Box sx={{ width: 20, height: 40, mr: 1 }}></Box>
                        <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: '#666',
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                            fontSize: '0.875rem',
                            background: 'transparent',
                            backgroundColor: 'transparent'
                        }}>
                            Lead
                        </Typography>
                    </Box>
                    <Box sx={{
                        flex: '0 0 7.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem 0.5rem',
                        background: 'transparent',
                        backgroundColor: 'transparent'
                    }}>
                        <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: '#666',
                            fontSize: '0.875rem',
                            background: 'transparent',
                            backgroundColor: 'transparent',
                            whiteSpace: 'nowrap'
                        }}>
                            Connected with
                        </Typography>
                    </Box>
                    <Box sx={{
                        flex: '0 0 6rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem 0.5rem',
                        background: 'transparent',
                        backgroundColor: 'transparent'
                    }}>
                        <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: '#666',
                            fontSize: '0.875rem',
                            background: 'transparent',
                            backgroundColor: 'transparent'
                        }}>
                            Type
                        </Typography>
                    </Box>
                    <Box sx={{
                        flex: '0 0 6rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem 0.5rem',
                        background: 'transparent',
                        backgroundColor: 'transparent'
                    }}>
                        <TableSortLabel
                            active={orderBy === 'date'}
                            direction={order}
                            onClick={handleRequestSort}
                            sx={{
                                color: '#666 !important',
                                '&.Mui-active': { color: '#666 !important' },
                                '& .MuiTableSortLabel-icon': { color: '#666 !important' },
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                background: 'transparent',
                                backgroundColor: 'transparent'
                            }}
                        >
                            <Typography variant="body2" sx={{
                                fontWeight: 600,
                                color: '#666',
                                fontSize: '0.875rem',
                                background: 'transparent',
                                backgroundColor: 'transparent'
                            }}>
                                Date
                            </Typography>
                        </TableSortLabel>
                    </Box>
                    <Box sx={{
                        flex: '0 0 4.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1rem 0.5rem',
                        background: 'transparent',
                        backgroundColor: 'transparent'
                    }}>
                        <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: '#666',
                            fontSize: '0.875rem',
                            background: 'transparent',
                            backgroundColor: 'transparent'
                        }}>
                            Export
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Table Data - Individual white containers for each lead */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {sortedLeads
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((lead) => {
                        if (!lead || !lead._id) {
                            console.error("Invalid lead in the array:", lead);
                            return null;
                        }

                        const isItemSelected = selected.indexOf(lead._id) !== -1;
                        const isRowHovered = hoveredRow === lead._id;

                        return (
                            <Paper
                                key={lead._id}
                                onClick={(event) => handleClick(event, lead)}
                                onMouseEnter={() => handleRowMouseEnter(lead._id)}
                                onMouseLeave={handleRowMouseLeave}
                                sx={{
                                    width: '100%',
                                    backgroundColor: 'white',
                                    borderRadius: '0.75rem',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    zIndex: 0,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#f8f9fa',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        minWidth: '62.5rem',
                                        padding: '0.5rem 0.75rem 0.5rem 1rem'
                                    }}
                                >
                                    <Box sx={{ flex: '0 0 25rem', display: 'flex', alignItems: 'center', padding: '0.5rem 0.75rem 0.5rem 1rem' }}>
                                        <Checkbox
                                            checked={isItemSelected}
                                            sx={{
                                                color: '#d0d0d0',
                                                padding: '0',
                                                marginRight: '0.75rem',
                                                '&.Mui-checked': {
                                                    color: '#a0a0a0'
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    borderRadius: '0.5rem',
                                                    backgroundColor: '#fff'
                                                }
                                            }}
                                        />
                                        <Avatar
                                            src={lead.profilePictureUrl || null}
                                            sx={{
                                                bgcolor: '#e0e0e0',
                                                width: 48,
                                                height: 48,
                                                mr: 2.5
                                            }}
                                        >
                                            {!lead.profilePictureUrl && getInitials(lead.leadName)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                {lead.leadName || lead.leadEmail || 'Unnamed Lead'}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem', mt: 0.5 }}>
                                                {lead.leadEmail || 'No Email'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ flex: '0 0 7.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Tooltip
                                                title={`Connected with ${leads.indexOf(lead) % 2 === 0 ? 'Alex Johnson' : 'Maria Garcia'}`}
                                                placement="top"
                                                arrow
                                            >
                                                <Avatar
                                                    src={`https://i.pravatar.cc/40?img=${(leads.indexOf(lead) % 10) + 20}`}
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        transition: 'transform 0.2s ease-in-out',
                                                        '&:hover': {
                                                            transform: 'scale(1.1)',
                                                            transformOrigin: 'center'
                                                        }
                                                    }}
                                                >
                                                    {leads.indexOf(lead) % 2 === 0 ? 'AJ' : 'MG'}
                                                </Avatar>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    <Box sx={{ flex: '0 0 6rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <LeadSourceChip source={lead.leadSource || 'manually-added'} />
                                    </Box>
                                    <Box sx={{ flex: '0 0 6rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                                            {lead.createdAt
                                                ? new Date(lead.createdAt).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                })
                                                : 'N/A'
                                            }
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: '0 0 4.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Tooltip title="Export to Salesforce">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    console.log('Export lead to Salesforce:', lead);
                                                }}
                                                sx={{ color: '#666' }}
                                            >
                                                <Salesforce sx={{ fontSize: '1rem' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        );
                    })}
            </Box>

            {/* Pagination */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={leads.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-toolbar': {
                            paddingLeft: 2,
                            paddingRight: 2
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

TeamV2LeadsTable.propTypes = {
    leads: PropTypes.array,
    onLeadClick: PropTypes.func,
    isLoading: PropTypes.bool,
    onRefresh: PropTypes.func,
    onSelectLeads: PropTypes.func,
    selectedLeadIds: PropTypes.array
};

export default TeamV2LeadsTable;